import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';
import ModalCrewTimesheet from './ModalCrewTimesheet';
import ProductionLoggedModal from './ProductionLoggedModal';
import InventoryTrackingModal from './InventoryTrackingModal';
import { useDailyLogFlow } from '../hooks/useDailyLogFlow';

const toRowData = (row) => ({
  scopeId: row?.scopeId,
  scope: row?.scope || 'Unnamed Scope',
  originalData: row,
});

const toActivityOption = (row) => ({
  scopeId: row?.scopeId,
  scope: row?.scope || 'Unnamed Scope',
  originalData: row,
});

/**
 * Global Crew Timesheet flow (Recent clipboard).
 * Opens immediately when Recent already chose an activity.
 */
function CrewTimesheetFlowHost() {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [isCrewOpen, setIsCrewOpen] = useState(false);
  const [projectCode, setProjectCode] = useState('');
  const [projectName, setProjectName] = useState('');
  const [rowData, setRowData] = useState(null);
  const [projectActivities, setProjectActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const {
    step: dailyLogStep,
    context: dailyLogContext,
    begin: beginDailyLog,
    close: closeDailyLog,
    navigate: navigateDailyLogStep,
    productionMounted,
    inventoryMounted,
    productionVisible,
    inventoryVisible,
  } = useDailyLogFlow();

  useEffect(() => {
    const onOpen = (event) => {
      const detail = event.detail || {};
      const code = String(detail.projectNo || detail.projectId || '').trim();
      if (!code) return;

      const incomingActivities = Array.isArray(detail.projectActivities)
        ? detail.projectActivities
        : [];
      const incomingRow = detail.rowData || null;

      setProjectCode(code);
      setProjectName(String(detail.projectName || '').trim());
      beginDailyLog();
      setIsCrewOpen(true);

      if (incomingRow) {
        setRowData(incomingRow);
        setProjectActivities(incomingActivities);
        setActivityLoading(false);
        return;
      }

      setRowData(null);
      setProjectActivities(incomingActivities);
      setActivityLoading(true);
    };

    window.addEventListener('openCrewTimesheet', onOpen);
    return () => window.removeEventListener('openCrewTimesheet', onOpen);
  }, [beginDailyLog]);

  useEffect(() => {
    if (!isCrewOpen || rowData || !projectCode || !isReady) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/miter/production-performance/${encodeURIComponent(
            projectCode
          )}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        const rows = res.data?.data?.rows;
        if (res.data?.status !== 'success' || !Array.isArray(rows) || rows.length === 0) {
          setActivityLoading(false);
          return;
        }
        setProjectActivities(rows.map(toActivityOption));
        setRowData(toRowData(rows[0]));
        setActivityLoading(false);
      } catch {
        if (!cancelled) setActivityLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAuthConfig, isCrewOpen, isReady, projectCode, rowData]);

  const closeCrew = useCallback(() => {
    setIsCrewOpen(false);
    setRowData(null);
    setProjectActivities([]);
    setActivityLoading(false);
    closeDailyLog();
  }, [closeDailyLog]);

  const navigateDailyLog = useCallback((step, patch) => {
    navigateDailyLogStep(step, {
      ...(patch || {}),
      projectActivities,
    });
  }, [navigateDailyLogStep, projectActivities]);

  return (
    <>
      {isCrewOpen && (
        <ModalCrewTimesheet
          isOpen={isCrewOpen}
          rowData={rowData}
          projectCode={projectCode}
          projectName={projectName}
          projectActivities={projectActivities}
          activityLoading={activityLoading}
          onClose={closeCrew}
          dailyLogStep={dailyLogStep}
          flowWorkDate={dailyLogContext?.workDate || ''}
          onFlowNavigate={navigateDailyLog}
          onLogProduction={(context) => {
            navigateDailyLog('production', context);
          }}
        />
      )}

      {productionMounted && (
        <ProductionLoggedModal
          isOpen={productionVisible}
          preserveWhenHidden
          initialContext={dailyLogContext}
          onClose={closeCrew}
          onFlowNavigate={navigateDailyLog}
          onLogInventory={(context) => {
            navigateDailyLog('inventory', context);
          }}
        />
      )}

      {inventoryMounted && (
        <InventoryTrackingModal
          isOpen={inventoryVisible}
          preserveWhenHidden
          initialContext={dailyLogContext}
          onClose={closeCrew}
          onFlowNavigate={navigateDailyLog}
        />
      )}
    </>
  );
}

export default CrewTimesheetFlowHost;
